import React, {useMemo, useState} from 'react';
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCalendarDays, faChevronLeft, faCompass, faCrow, faSignsPost} from "@fortawesome/free-solid-svg-icons";
import {pluralize} from "../../helpers/helpers";
import {ExplorerHero, ExplorerHeroStat, ExplorerHeroStats} from "../../components/explorer-hero";
import {ChecklistSearch} from "../../components/checklist-search";
import {getHotspotExplorerHeroImage} from "./helpers";
import type {HotspotExplorerGroup, HotspotExplorerHotspot, HotspotExplorerSortMode, HotspotExplorerStats} from "./types";
import {HotspotTable} from "./hotspot-table";
import './hotspot-explorer-shell.scss';

type HotspotExplorerShellProps = {
    eyebrow: string,
    title: string,
    intro: string,
    backgroundImage?: string,
    backLabel?: string,
    onBack?: () => void,
    stats: HotspotExplorerStats,
    childStatLabel?: string,
    showChildStat?: boolean,
    showYearsExploring?: boolean,
    panelHeading: string,
    groups: HotspotExplorerGroup[],
    activeGroupCode?: string,
    onSelectGroup: (code: string) => void,
    hotspots: HotspotExplorerHotspot[],
    sortMode: HotspotExplorerSortMode,
    onChangeSort: (sortMode: HotspotExplorerSortMode) => void,
    onOpenHotspot: (locationId: string) => void
}

export function HotspotExplorerShell({
    eyebrow,
    title,
    intro,
    backgroundImage,
    backLabel,
    onBack,
    stats,
    childStatLabel,
    showChildStat,
    showYearsExploring: showYearsExploringProp,
    panelHeading,
    groups,
    activeGroupCode,
    onSelectGroup,
    hotspots,
    sortMode,
    onChangeSort,
    onOpenHotspot
}: HotspotExplorerShellProps) {
    const heroImage = useMemo(() => backgroundImage ?? getHotspotExplorerHeroImage(), [backgroundImage]);
    const childStatVisible = showChildStat ?? true;
    const showYearsExploring = showYearsExploringProp ?? true;
    const [searchTerm, setSearchTerm] = useState('');

    return (
        <section className="hotspot-explorer">
            <ExplorerHero
                className="explorer-hero--hotspot"
                backgroundImage={heroImage}
                backLabel={backLabel}
                onBack={onBack}
                eyebrow={eyebrow}
                title={title}
                intro={intro}
            >
                <ExplorerHeroStats ariaLabel="Hotspot summary">
                    {childStatVisible ? (
                        <ExplorerHeroStat
                            icon={<FontAwesomeIcon icon={faSignsPost} />}
                            label={childStatLabel ?? stats.childLabel}
                            value={stats.childCount.toLocaleString()}
                        />
                    ) : null}
                    <ExplorerHeroStat
                        icon={<FontAwesomeIcon icon={faCompass} />}
                        label={pluralize(hotspots.length, 'Hotspot Visited', 'Hotspots Visited')}
                        value={hotspots.length.toLocaleString()}
                    />
                    <ExplorerHeroStat
                        icon={<FontAwesomeIcon icon={faCrow} />}
                        label="Species Seen"
                        value={stats.speciesSeen.toLocaleString()}
                    />
                    {showYearsExploring ?
                        <ExplorerHeroStat
                            icon={<FontAwesomeIcon icon={faCalendarDays} />}
                            label={pluralize(stats.yearsExploring, 'Different Year', 'Different Years')}
                            value={stats.yearsExploring.toLocaleString()}
                        />
                        : null
                    }
                </ExplorerHeroStats>
            </ExplorerHero>

            <div className="hotspot-explorer__content">
                <div className="hotspot-explorer__list-panel">
                    <div className="hotspot-explorer__list-panel-header">
                        <div className="hotspot-explorer__list-toolbar">
                            <ChecklistSearch
                                label="Search hotspots"
                                value={searchTerm}
                                placeholder="Search hotspots..."
                                onChange={setSearchTerm}
                            />
                            <div className="hotspot-explorer__sort-tabs" aria-label="Hotspot sort options">
                                <button
                                    type="button"
                                    className={`hotspot-explorer__sort-tab ${sortMode === 'visits' ? 'is-active' : ''}`}
                                    onClick={() => onChangeSort('visits')}
                                >
                                    Most Visited
                                </button>
                                <button
                                    type="button"
                                    className={`hotspot-explorer__sort-tab ${sortMode === 'species' ? 'is-active' : ''}`}
                                    onClick={() => onChangeSort('species')}
                                >
                                    Most Species
                                </button>
                                <button
                                    type="button"
                                    className={`hotspot-explorer__sort-tab ${sortMode === 'recent' ? 'is-active' : ''}`}
                                    onClick={() => onChangeSort('recent')}
                                >
                                    Most Recent
                                </button>
                            </div>
                        </div>
                        <span>Showing {hotspots.length.toLocaleString()} hotspots</span>
                    </div>
                    <HotspotTable hotspots={hotspots} searchTerm={searchTerm} onOpenHotspot={onOpenHotspot} />
                </div>

                <aside className="hotspot-explorer__panel" aria-label="Explore Your Places">
                    {groups.length ? (
                        <section className="hotspot-explorer__panel-section">
                            <h3 className="hotspot-explorer__panel-heading">{panelHeading}</h3>
                            <ul className="hotspot-explorer__group-list">
                                {groups.map(group => (
                                    <li key={group.code}>
                                        <button
                                            type="button"
                                            className={`hotspot-explorer__group-button ${activeGroupCode === group.code ? 'is-active' : ''}`}
                                            onClick={() => onSelectGroup(group.code)}
                                        >
                                            <span>{group.label}</span>
                                            <strong>{group.count.toLocaleString()}</strong>
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </section>
                    ) : (
                        <div className="hotspot-explorer__panel-empty">
                            <strong>No further subdivisions available</strong>
                            <span>This place only has hotspot-level data, so there is nothing else to drill into here.</span>
                        </div>
                    )}
                </aside>
            </div>
        </section>
    );
}
